import React from 'react'

import CircularProgress from '@mui/material/CircularProgress'
import Stack from '@mui/material/Stack'
import Button from '@mui/material/Button'
import TableContainer from '@mui/material/TableContainer'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import TextField from '@mui/material/TextField'
import Divider from '@mui/material/Divider'
import LoadingButton from '@mui/lab/LoadingButton'
import TimelineOutlinedIcon from '@mui/icons-material/TimelineOutlined'
import ScaleOutlinedIcon from '@mui/icons-material/ScaleOutlined'
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { StaticDatePicker } from '@mui/x-date-pickers/StaticDatePicker'

import { Iteration, Schedule } from '../../types/schedule'
import { Inventory } from '../../types/inventory'
import { PagedResponse } from '../../types/pagedResponse'
import { get } from '../../utils/service'
import { Profile } from '../../types/profile'
import { ScheduleStatus } from '../../types/enums/scheduleStatus'
import { toReadableTimestamp, getDateString } from '../../utils/dateUtils'
import TimePicker from '../../components/schedule/timePicker'
import {
  kgToPounds,
  poundsToKg,
  getBigWeightSuffix,
  getP2000MaxWeight,
  getP2000MinWeight,
} from '../../utils/weightUtils'
import ScheduleWizardButton from '../../components/schedule/scheduleWizardButton'
import Alert from '../../components/alert/alert'
import ProfileTypeChip from '../../components/profiles/profileTypeChip'
import SearchField from '../../components/logs/logSearchField'
import BeanIcon from '../../svgicons/bean'
import NumberField from '../fields/numberField'
import Pagination from '../fields/pagination'

interface ScheduleWizardProps {
  schedule: Schedule
  isSaving: boolean
  machineId: number
  customerId: number
  onSaveSchedule: (schedule: Schedule) => void
}

const getInventoryUrl = (customerId: number, searchString: string): string => {
  const searchFragment = searchString ? `&search=${searchString}` : ''
  return `inventories/?customer=${customerId}${searchFragment}&is_archived=false`
}

const getProfilesUrl = (customerId: number, searchString: string): string => {
  const searchFragment = searchString ? `&search=${searchString}` : ''
  return `profiles/?customer=${customerId}${searchFragment}&machine_type=p2000&is_bpp_profile=false`
}

const getSelectedDay = (date: Date): string => {
  const selectedDay = new Date(date)
  selectedDay.setHours(0, 0, 0, 0)
  return getDateString(selectedDay)
}

const ScheduleWizard = (props: ScheduleWizardProps): JSX.Element => {
  const [currentStep, setCurrentStep] = React.useState<number>(1)

  const [selectedInventory, setSelectedInventory] = React.useState<Inventory | null>(props.schedule.inventory_data)
  const [selectedProfile, setSelectedProfile] = React.useState<Profile | null>(props.schedule.profile_data)
  const [iterationWeight, setIterationWeight] = React.useState<number>(
    props.schedule.iterations.length > 0 ? kgToPounds(props.schedule.iterations[0].weight / 1000) : 0
  )
  const [hasIterationsError, setHasIterationsError] = React.useState<boolean>(false)
  const [selectedIterations, setSelectedIterations] = React.useState<Array<Iteration>>(props.schedule.iterations)
  const [selectedDate, setSelectedDate] = React.useState<Date>(props.schedule.date)

  const [currentSelectedDay, setCurrentSelectedDay] = React.useState<string>(getSelectedDay(props.schedule.date))
  const [isLoadingDaySchedule, setIsLoadingDaySchedule] = React.useState<boolean>(false)
  const [hasLoadingDayScheduleError, setHasLoadingDayScheduleError] = React.useState<boolean>(false)
  const [daySchedules, setDaySchedules] = React.useState<Array<Schedule> | null>(null)

  const [inventorySearchString, setInventorySearchString] = React.useState<string>('')
  const [loadInventoryUrl, setLoadInventoryUrl] = React.useState<string>('')
  const [inventoryPaginationSettings, setInventoryPaginationSettings] = React.useState<PagedResponse<Inventory> | null>(
    null
  )
  const [inventory, setInventory] = React.useState<Array<Inventory> | null>(null)
  const [isLoadingInventory, setIsLoadingInventory] = React.useState<boolean>(false)
  const [hasInventoryError, setHasInventoryError] = React.useState<boolean>(false)

  const [profilesSearchString, setProfilesSearchString] = React.useState<string>('')
  const [loadProfilesUrl, setLoadProfilesUrl] = React.useState<string>('')
  const [profilesPaginationSettings, setProfilesPaginationSettings] = React.useState<PagedResponse<Profile> | null>(
    null
  )
  const [profiles, setProfiles] = React.useState<Array<Profile> | null>(null)
  const [isLoadingProfiles, setIsLoadingProfiles] = React.useState<boolean>(false)
  const [hasProfilesError, setHasProfilesError] = React.useState<boolean>(false)

  React.useEffect(() => {
    if (loadInventoryUrl) {
      setInventory(null)
      setIsLoadingInventory(true)
      get<PagedResponse<Inventory>>(loadInventoryUrl)
        .then((response) => {
          setIsLoadingInventory(false)
          setInventoryPaginationSettings(response.data)
          setInventory(response.data.results)
        })
        .catch(() => {
          setIsLoadingInventory(false)
          setHasInventoryError(true)
        })
    }
  }, [loadInventoryUrl])

  React.useEffect(() => {
    if (props.customerId) {
      setLoadInventoryUrl(getInventoryUrl(props.customerId, inventorySearchString))
    }
  }, [inventorySearchString, props.customerId])

  React.useEffect(() => {
    if (loadProfilesUrl) {
      setProfiles(null)
      setIsLoadingProfiles(true)
      get<PagedResponse<Profile>>(loadProfilesUrl)
        .then((response) => {
          setIsLoadingProfiles(false)
          setProfilesPaginationSettings(response.data)
          setProfiles(response.data.results.filter((x) => !x.is_bbp_profile))
        })
        .catch(() => {
          setIsLoadingProfiles(false)
          setHasProfilesError(true)
        })
    }
  }, [loadProfilesUrl])

  React.useEffect(() => {
    if (props.customerId) {
      setLoadProfilesUrl(getProfilesUrl(props.customerId, profilesSearchString))
    }
  }, [profilesSearchString, props.customerId])

  React.useEffect(() => {
    if (currentSelectedDay && props.machineId && currentStep === 4) {
      setIsLoadingDaySchedule(true)
      setHasLoadingDayScheduleError(false)
      setDaySchedules(null)
      get<Array<Schedule>>(`schedules/?machine=${props.machineId}&page_size=all&date=${currentSelectedDay}`)
        .then((response) => {
          setIsLoadingDaySchedule(false)
          setDaySchedules(response.data)
        })
        .catch(() => {
          setIsLoadingDaySchedule(false)
          setHasLoadingDayScheduleError(true)
        })
    }
  }, [currentSelectedDay, props.machineId, currentStep])

  const getMaxConfiguredStep = (): number => {
    if ((selectedIterations.length || 0) > 0) {
      return 4
    } else if (selectedProfile) {
      return 3
    } else if (selectedInventory) {
      return 2
    }
    return 1
  }

  const saveSchedule = () => {
    const saveData = {
      ...props.schedule,
      inventory: selectedInventory?.url || '',
      inventory_data: selectedInventory,
      profile: selectedProfile?.url || '',
      profile_data: selectedProfile,
      iterations: selectedIterations.map((x) => {
        return {
          ...x,
          weight: Math.round(poundsToKg(x.weight) * 1000),
        }
      }),
      date: selectedDate,
    }
    props.onSaveSchedule(saveData)
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Stack direction="row" mb={3} mt={1}>
        <ScheduleWizardButton
          icon={<BeanIcon />}
          name="BEAN"
          subtitle={selectedInventory?.name || 'Name of bean'}
          step={1}
          currentStep={currentStep}
          maxConfiguredStep={getMaxConfiguredStep()}
          onClick={() => setCurrentStep(1)}
        />
        <ScheduleWizardButton
          icon={<TimelineOutlinedIcon />}
          name="PROFILE"
          subtitle={selectedProfile?.name || 'Name of profile'}
          step={2}
          currentStep={currentStep}
          maxConfiguredStep={getMaxConfiguredStep()}
          onClick={() => setCurrentStep(2)}
        />
        <ScheduleWizardButton
          icon={<ScaleOutlinedIcon />}
          name="VOLUME"
          subtitle={
            selectedIterations.length > 0
              ? `${selectedIterations.length} x ${iterationWeight.toFixed(2)} ${getBigWeightSuffix()}`
              : 'Iterations'
          }
          step={3}
          currentStep={currentStep}
          maxConfiguredStep={getMaxConfiguredStep()}
          onClick={() => setCurrentStep(3)}
        />
        <ScheduleWizardButton
          icon={<CalendarMonthOutlinedIcon />}
          name="SCHEDULE"
          subtitle={selectedDate ? toReadableTimestamp(selectedDate) : 'Now or later'}
          step={4}
          currentStep={currentStep}
          maxConfiguredStep={getMaxConfiguredStep()}
          onClick={() => setCurrentStep(4)}
        />
      </Stack>
      <Divider sx={{ mb: 3 }} />
      {currentStep === 1 && (
        <>
          {inventory && (
            <TableContainer>
              <Table className="colored-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Inventory name</TableCell>
                    <TableCell>
                      <Stack alignItems="flex-end">
                        <SearchField
                          placeholder="Search inventory"
                          initialSearchString={inventorySearchString}
                          onSearch={(searchString: string): void => {
                            const newUrl = getInventoryUrl(props.customerId || 0, searchString)
                            if (newUrl !== loadInventoryUrl) {
                              setInventoryPaginationSettings(null)
                              setInventorySearchString(searchString)
                            }
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {inventory.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Alert severity="info">No inventory found</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                  {inventory.map((x) => {
                    return (
                      <TableRow key={x.url} className="colored-row">
                        <TableCell>
                          <Button
                            className="link-cell"
                            onClick={() => {
                              setSelectedInventory(x)
                              setCurrentStep(currentStep + 1)
                            }}
                          />
                          {x.name}
                        </TableCell>
                        <TableCell sx={{ textAlign: 'right' }}></TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {hasInventoryError && <Alert severity="error">Could not load inventory</Alert>}
          {isLoadingInventory && <CircularProgress />}
          {inventoryPaginationSettings && (
            <Pagination
              isLoading={isLoadingInventory}
              paginationSettings={inventoryPaginationSettings}
              onChangePage={(newPageUrl: string) => setLoadInventoryUrl(newPageUrl)}
            />
          )}
        </>
      )}
      {currentStep === 2 && (
        <>
          {profiles && (
            <TableContainer>
              <Table className="colored-table">
                <TableHead>
                  <TableRow>
                    <TableCell>Profile name</TableCell>
                    <TableCell>Profile type</TableCell>
                    <TableCell>
                      <Stack alignItems="flex-end">
                        <SearchField
                          placeholder="Search profiles"
                          initialSearchString={profilesSearchString}
                          onSearch={(searchString: string): void => {
                            const newUrl = getProfilesUrl(props.customerId || 0, searchString)
                            if (newUrl !== loadProfilesUrl) {
                              setProfilesPaginationSettings(null)
                              setProfilesSearchString(searchString)
                            }
                          }}
                        />
                      </Stack>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {profiles.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={10}>
                        <Alert severity="info">No profiles found</Alert>
                      </TableCell>
                    </TableRow>
                  )}
                  {profiles.map((x) => {
                    return (
                      <TableRow key={x.url} className="colored-row">
                        <TableCell>
                          <Button
                            className="link-cell"
                            onClick={() => {
                              setSelectedProfile(x)
                              setCurrentStep(currentStep + 1)
                            }}
                          />
                          {x.name}
                        </TableCell>
                        <TableCell>
                          <ProfileTypeChip profile={x} />
                        </TableCell>
                        <TableCell />
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {hasProfilesError && <Alert severity="error">Could not load profiles</Alert>}
          {isLoadingProfiles && <CircularProgress />}
          {profilesPaginationSettings && (
            <Pagination
              isLoading={isLoadingProfiles}
              paginationSettings={profilesPaginationSettings}
              onChangePage={(newPageUrl: string) => setLoadProfilesUrl(newPageUrl)}
            />
          )}
        </>
      )}
      {currentStep === 3 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <NumberField
            label="Batch size"
            valueSuffix={getBigWeightSuffix()}
            decimalPlaces={2}
            numValue={iterationWeight}
            minNumValue={getP2000MinWeight()}
            maxNumValue={getP2000MaxWeight()}
            onBlur={(newValue: number): void => {
              setIterationWeight(newValue)
              if (selectedIterations.length > 0) {
                setSelectedIterations((oldIterations) =>
                  oldIterations.map((x) => {
                    return {
                      ...x,
                      weight: newValue,
                    }
                  })
                )
              }
            }}
          />
          <NumberField
            label="Iterations"
            valueSuffix=""
            numValue={selectedIterations.length}
            minNumValue={1}
            maxNumValue={20}
            onBlur={(newValue: number): void => {
              const iterations: Array<Iteration> = []
              for (let i = 0; i < newValue; i++) {
                iterations.push({
                  url: '',
                  weight: iterationWeight,
                  status: ScheduleStatus.Scheduled,
                  log: '',
                  log_data: null,
                })
              }
              setSelectedIterations(iterations)
            }}
          />
          <Button
            variant="contained"
            onClick={(): void => {
              if (selectedIterations.length > 0 && iterationWeight > 0) {
                setHasIterationsError(false)
                setCurrentStep(currentStep + 1)
              } else {
                setHasIterationsError(true)
              }
            }}
          >
            Next
          </Button>
          {hasIterationsError && <Alert severity="error">Batch size and iterations must be set</Alert>}
        </Stack>
      )}
      {currentStep === 4 && (
        <Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} flex={1}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              label="Date"
              inputFormat="dd/MM/yyyy"
              onChange={(newDate: Date | null) => {
                setCurrentSelectedDay(getDateString(newDate || new Date()))
                setSelectedDate(newDate || new Date()) // date cannot be set to null)
              }}
              value={selectedDate ? new Date(selectedDate) : null}
              renderInput={(params) => <TextField {...params} required />}
            />
            {isLoadingDaySchedule && <CircularProgress />}
            {hasLoadingDayScheduleError && <Alert severity="error">Could not load scheduled roasts</Alert>}
            {daySchedules && (
              <TimePicker
                selectedDate={selectedDate || null}
                duration={selectedIterations.length || 0}
                daySchedules={daySchedules.filter((x) => x.url !== props.schedule.url)}
                setSelectedTime={(hours: number, minutes: number) => {
                  setSelectedDate((currentDate) => {
                    const date = currentDate ? new Date(currentDate) : new Date()
                    date.setHours(hours, minutes)
                    return date
                  })
                }}
              />
            )}
          </Stack>
          <LoadingButton
            sx={{ alignSelf: 'flex-end', mt: 2 }}
            variant="contained"
            disabled={!selectedDate}
            loading={props.isSaving}
            onClick={saveSchedule}
          >
            {props.schedule.url ? 'Update scheduled roast' : 'Add scheduled roast'}
          </LoadingButton>
        </Stack>
      )}
    </LocalizationProvider>
  )
}

export default ScheduleWizard
